/**
 * Pure formatters — currency, weight, dates. Kept dependency-free
 * (no date-fns, no Intl.PluralRules theatrics) so the web bundle
 * stays under 250KB gzipped.
 */

export function formatPrice(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '—'
  return `$${Math.round(usd).toLocaleString()}`
}

// Price digits WITHOUT the "$" — for treatments that render the currency
// symbol as a separate (smaller, sans) glyph. The Playfair "$" is a tall
// bar-through-S glyph that looks broken at large display sizes, so the modal
// splits it out. Returns '' for missing so callers can decide the fallback.
export function formatPriceNumber(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return ''
  return Math.round(usd).toLocaleString()
}

// The supplier's OWN stock reference (refStockId) is what buyers should see —
// never Aska's internal TagNo (AJ####). Signed-in buyers always have
// refStockId (100% of the live catalog); it's security-masked to anon, so the
// internal tag is used ONLY as an anon-teaser fallback so cards still label.
export function displayStockId(stone: { refStockId?: string | null; stockId: string }): string {
  return stone.refStockId ?? stone.stockId
}

export function formatPerCarat(perCt: number | null | undefined): string {
  if (perCt == null || !Number.isFinite(perCt)) return '—'
  return `$${Math.round(perCt).toLocaleString()}/ct`
}

export function formatCarat(carat: number | null | undefined): string {
  if (carat == null || !Number.isFinite(carat)) return '—'
  return `${Number(carat).toFixed(2)}ct`
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Localized "Month YYYY" for the profile "Member since" line (e.g.
 * "August 2026"). Returns null — NOT a placeholder — when the timestamp is
 * missing or unparseable, so callers OMIT the line for older/migrated
 * accounts rather than render "Invalid Date" / "Member since undefined".
 */
export function formatMemberSince(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })
}

export function pluralize(count: number, one: string, many: string): string {
  return count === 1 ? `${count} ${one}` : `${count} ${many}`
}
